import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/services/BaseService";
import { legalPagesService } from "@/services/legalPagesService";
import {
  CreateLegalPageDto,
  LegalPageType,
  UpdateLegalPageDto,
} from "@/types/legal-page";

export const legalPageKeys = {
  all: ["legal-pages"] as const,
  lists: () => [...legalPageKeys.all, "list"] as const,
  list: () => [...legalPageKeys.lists()] as const,
  details: () => [...legalPageKeys.all, "detail"] as const,
  detail: (type: LegalPageType, language: string) =>
    [...legalPageKeys.details(), type, language] as const,
};

export const useLegalPages = () =>
  useQuery({
    queryKey: legalPageKeys.list(),
    queryFn: () => legalPagesService.getLegalPages(),
  });

export const useLegalPage = (type: LegalPageType, language: string) =>
  useQuery({
    queryKey: legalPageKeys.detail(type, language),
    queryFn: () => legalPagesService.getLegalPage(`${type}-${language}`),
    retry: false,
  });

export const useCreateLegalPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLegalPageDto) =>
      legalPagesService.createLegalPage(data),
    onSuccess: (legalPage) => {
      queryClient.invalidateQueries({ queryKey: legalPageKeys.lists() });

      const match = legalPage.key.match(/^(privacy-policy|terms-of-use)-(.+)$/);

      if (!match) {
        return;
      }

      const [, type, language] = match;

      queryClient.setQueryData(
        legalPageKeys.detail(type as LegalPageType, language),
        legalPage,
      );
    },
  });
};

export const useUpdateLegalPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      data,
    }: {
      key: string;
      language: string;
      data: UpdateLegalPageDto;
    }) => legalPagesService.updateLegalPage(key, data),
    onSuccess: (legalPage, variables) => {
      queryClient.invalidateQueries({ queryKey: legalPageKeys.lists() });
      queryClient.setQueryData(
        legalPageKeys.detail(
          variables.key.replace(/-[^-]+$/, "") as LegalPageType,
          variables.language,
        ),
        legalPage,
      );
    },
  });
};

export const isLegalPageMissingError = (error: unknown) =>
  error instanceof ApiError && error.statusCode === 404;
